import { useState } from "react"
import i18next from "i18next"
import { type ValidationError } from "../../Objects/Generics/Helps"
import { CompanyMetaSettingsEntity } from "../../Objects/Meta/CompanyMetaSettingsEntity"

export interface SaveMetaIntegrationPayload {
  accountSid: string
  authToken: string
  whatsappFromNumber: string
  isEnabled: boolean
  isStatusChangedNotificationEnabled: boolean
  isFifteenDaysCampaignEnabled: boolean
  isPrizeNotificationEnabled: boolean
  statusChangedTemplate: string
  fifteenDaysCampaignTemplate: string
  prizeTemplate: string
  fifteenDaysIntervalDays: number
}

export const useCreateMetaIntegration = () => {
  const [accountSid, setAccountSid] = useState<string>("")

  const [authToken, setAuthToken] = useState<string>("")

  const [whatsappFromNumber, setWhatsappFromNumber] = useState<string>("")

  const [isEnabled, setIsEnabled] = useState<boolean>(false)

  const [status, setStatus] = useState<string>("")

  const [metaSettings, setMetaSettings] = useState<CompanyMetaSettingsEntity | null>(null)

  const [isStatusChangedNotificationEnabled, setIsStatusChangedNotificationEnabled] = useState<boolean>(false)

  const [isFifteenDaysCampaignEnabled, setIsFifteenDaysCampaignEnabled] = useState<boolean>(false)

  const [isPrizeNotificationEnabled, setIsPrizeNotificationEnabled] = useState<boolean>(false)

  const [statusChangedTemplate, setStatusChangedTemplate] = useState<string>("")

  const [fifteenDaysCampaignTemplate, setFifteenDaysCampaignTemplate] = useState<string>("")

  const [prizeTemplate, setPrizeTemplate] = useState<string>("")

  const [fifteenDaysIntervalDays, setFifteenDaysIntervalDays] = useState<number>(15)

  const handleAccountSidChange = (e: React.ChangeEvent<HTMLInputElement>) => setAccountSid(e.target.value)

  const handleAuthTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => setAuthToken(e.target.value)

  const handleWhatsappFromNumberChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setWhatsappFromNumber(e.target.value)

  const handleEnabledChange = (value: boolean) => setIsEnabled(value)

  const handleStatusChange = (value: string) => setStatus(value)

  const handleInitialValue = (settings: CompanyMetaSettingsEntity | null | undefined) => {
    if (!settings) {
      return
    }

    setMetaSettings(settings)

    setAccountSid(settings.accountSid || "")

    setWhatsappFromNumber(settings.whatsappFromNumber || "")

    setIsEnabled(settings.isEnabled)

    setStatus(
      settings.isValidated
        ? i18next.t("company:label_meta_status_connected")
        : settings.lastValidationErrorMessage || i18next.t("company:label_meta_status_disconnected"),
    )

    setIsStatusChangedNotificationEnabled(settings.isStatusChangedNotificationEnabled)

    setIsFifteenDaysCampaignEnabled(settings.isFifteenDaysCampaignEnabled)

    setIsPrizeNotificationEnabled(settings.isPrizeNotificationEnabled)

    setStatusChangedTemplate(settings.statusChangedTemplate)

    setFifteenDaysCampaignTemplate(settings.fifteenDaysCampaignTemplate)

    setPrizeTemplate(settings.prizeTemplate)

    setFifteenDaysIntervalDays(settings.fifteenDaysIntervalDays || 15)
  }

  const handleSubmitForm = (): SaveMetaIntegrationPayload | { errors: ValidationError[] } => {
    const errors: ValidationError[] = []

    if (accountSid.trim().length === 0) {
      errors.push({
        field: i18next.t("company:label_meta_account_sid"),
        message: i18next.t("company:error_meta_account_sid_required"),
      })
    }

    if (whatsappFromNumber.trim().length === 0) {
      errors.push({
        field: i18next.t("company:label_meta_whatsapp_from"),
        message: i18next.t("company:error_meta_whatsapp_from_required"),
      })
    }

    if (errors.length > 0) {
      return { errors }
    }

    return {
      accountSid: accountSid.trim(),
      authToken: authToken.trim(),
      whatsappFromNumber: whatsappFromNumber.trim(),
      isEnabled,
      isStatusChangedNotificationEnabled,
      isFifteenDaysCampaignEnabled,
      isPrizeNotificationEnabled,
      statusChangedTemplate: statusChangedTemplate.trim(),
      fifteenDaysCampaignTemplate: fifteenDaysCampaignTemplate.trim(),
      prizeTemplate: prizeTemplate.trim(),
      fifteenDaysIntervalDays,
    }
  }

  return {
    accountSid,
    authToken,
    whatsappFromNumber,
    isEnabled,
    status,
    metaSettings,
    isStatusChangedNotificationEnabled,
    isFifteenDaysCampaignEnabled,
    isPrizeNotificationEnabled,
    statusChangedTemplate,
    fifteenDaysCampaignTemplate,
    prizeTemplate,
    fifteenDaysIntervalDays,
    handleAccountSidChange,
    handleAuthTokenChange,
    handleWhatsappFromNumberChange,
    handleEnabledChange,
    handleStatusChange,
    handleSubmitForm,
    handleInitialValue,
    setIsStatusChangedNotificationEnabled,
    setIsFifteenDaysCampaignEnabled,
    setIsPrizeNotificationEnabled,
    setStatusChangedTemplate,
    setFifteenDaysCampaignTemplate,
    setPrizeTemplate,
    setFifteenDaysIntervalDays,
  }
}
